// Data structures and logic for describing the current page contents.

// Information about an element on the page.
export interface PageContentsElement {
  // Value of data-testid for the element.
  testId: string;

  // HTML tag name of the element.
  tagName: string;

  // Any text on the element which is not associated with child elements.
  innerText: string;

  // Any child elements.
  children?: PageContentsElement[];
}

// Information about all content on the page.
export interface PageContents {
  // Current value of window.location.href.
  locationHref: string;

  // Top level elements on the page.
  elements: PageContentsElement[];
}

export async function getPageContents(): Promise<PageContents> {
  const locationHref = window.location.href;
  const elements: PageContentsElement[] = [];

  // Crawl the DOM starting from document.body
  if (document.body) {
    crawlElement(document.body, elements, null);
  }

  return {
    locationHref,
    elements,
  };
}

// Recursively crawl an element and its children
function crawlElement(
  element: Element,
  topLevelElements: PageContentsElement[],
  parentPageElement: PageContentsElement | null
): void {
  const testId = element.getAttribute("data-testid");

  let currentPageElement: PageContentsElement | null = null;

  if (testId) {
    // Create a PageContentsElement for this element
    currentPageElement = {
      testId,
      tagName: element.tagName.toLowerCase(),
      innerText: "",
      children: [],
    };

    // If there's no parent with data-testid, this is a top-level element
    if (!parentPageElement) {
      topLevelElements.push(currentPageElement);
    } else {
      // Add as child of parent
      parentPageElement.children = parentPageElement.children || [];
      parentPageElement.children.push(currentPageElement);
    }
  }

  // Extract innerText from direct text nodes and elements without data-testid
  if (currentPageElement) {
    currentPageElement.innerText = extractDirectText(element);
  }

  // Recursively process children
  for (const child of Array.from(element.children)) {
    crawlElement(child, topLevelElements, currentPageElement || parentPageElement);
  }
}

// Extract text content that is directly in the element, excluding text from child elements with data-testid
function extractDirectText(element: Element): string {
  let text = "";

  // Walk through child nodes
  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Direct text node
      const textContent = node.textContent?.trim() || "";
      if (textContent) {
        text += (text ? " " : "") + textContent;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const childElement = node as Element;
      // Only include text from child elements that don't have data-testid
      if (!childElement.hasAttribute("data-testid")) {
        const childText = extractDirectText(childElement);
        if (childText) {
          text += (text ? " " : "") + childText;
        }
      }
    }
  }

  return text.trim();
}
